import React, { ReactNode } from 'react'
import styled from '@emotion/styled'
import Button from 'library/atoms/Button'
import Flex from 'shared/library/atoms/Flex'
import { Text } from 'library/atoms/Typography'
import { css } from '@emotion/react'
import Link from 'next/link'

interface ContentHeroProps {
  backgroundColor?: string
  title?: ReactNode
  content?: ReactNode
  links?: Array<{ text: string; link: any }>
  list?: Array<{ icon: any; text: string }>
  image?: ReactNode
}

const ContentHero = ({
  backgroundColor,
  title,
  content,
  links,
  list,
  image
}: ContentHeroProps = {}) => {
  return (
    <SectionContainer color={backgroundColor}>
      <Section>
        <StyledFlex>
          <ColumnLeft>
            {title}
            {content}
            {links && (
              <Flex alignItems="center" gap={3} mt={2}>
                {links.map((item: any, index: number) => (
                  <Link key={`slice-links-${index}`} href={item.link}>
                    {index === 0 ? (
                      <Button variant="primary">{item.text}</Button>
                    ) : (
                      <>{item.text}</>
                    )}
                  </Link>
                ))}
              </Flex>
            )}

            {list && (
              <Flex alignItems="flex-start" gap={4} mt={2}>
                {list.map((item: any, index: number) => (
                  <HeroIcon key={`slice-list-${index}`} gap={1}>
                    {item.icon}
                    <Text fontStyle="p3" color="secondary">
                      {item.text}
                    </Text>
                  </HeroIcon>
                ))}
              </Flex>
            )}
          </ColumnLeft>
          <ColumnRight includeBg={!image}>
            <ImagePortal>
              <MainImageContainer>{image}</MainImageContainer>
            </ImagePortal>
          </ColumnRight>
        </StyledFlex>
      </Section>
    </SectionContainer>
  )
}

export default ContentHero

const SectionContainer = styled.section(({ color }) => ({
  backgroundColor: `${color}`,
  padding: '20px 0'
}))

const Section = styled.div(({ theme }) => ({
  padding: theme.mixins.pxSpread([0, theme.space[5]]),
  ...theme.mixins.centeredContainer('xxxl'),
  paddingTop: '20px',
  paddingBottom: '2opx'
}))

const ColumnLeft = styled(Flex)(() =>
  css({
    flexDirection: 'column',
    flex: 1,
    gap: 5
  })
)

const StyledFlex = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  gap: 5,
  justifyContent: 'center',
  alignItems: 'center',

  [theme.mediaQueries.lg]: {
    flexDirection: 'row'
  }
}))

const ColumnRight = styled(Flex)(({ includeBg }: { includeBg: boolean }) =>
  css({
    flexDirection: 'column',
    flex: 1,
    gap: 3,
    backgroundImage: includeBg ? 'url("/eola-hexagons.png")' : 'none',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 35% bottom 45%',
    minHeight: '300px'
  })
)

const ImagePortal = styled(Flex)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  padding: theme.space[5],
  gap: 3,

  justifyContent: 'center',
  [theme.mediaQueries.lg]: {
    justifyContent: 'right',
    paddingRight: 0
  }
}))

const MainImageContainer = styled.div(() =>
  css({
    position: 'relative',
    zIndex: 1
  })
)

const HeroIcon = styled(Flex)(() =>
  css({
    maxWidth: '110px',
    alignItems: 'center',
    textAlign: 'center',
    flexDirection: 'column'
  })
)
